// src/ds/StatusBadge.tsx
// Single source of truth for status across every table & detail header in the
// new console. Never hand-roll a coloured tag — import this.
import React from "react";
import type { TxStatus } from "./types";
import { STATUS } from "./tokens";

export interface StatusBadgeProps {
  status: TxStatus;
  size?: "sm" | "md";
  /**
   * A domain's own word for one of the shared states ("Our move" is `review`).
   * Only the words change: the colour still comes from STATUS, so a page that
   * needs its own label never has to hand-roll a tag to get one.
   */
  label?: string;
}

/** Colour is never the only signal — the label is always present (WCAG AA). */
export function StatusBadge({ status, size = "sm", label }: StatusBadgeProps) {
  const t = { ...STATUS[status], ...(label ? { label } : {}) };
  const pad = size === "md" ? "4px 11px 4px 9px" : "3px 9px 3px 8px";
  const fs = size === "md" ? 12.5 : 11.5;
  return (
    <span
      role="status"
      aria-label={t.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: pad,
        borderRadius: 999,
        background: t.bg,
        color: t.fg,
        fontSize: fs,
        fontWeight: 600,
        whiteSpace: "nowrap",
        lineHeight: 1.2,
      }}
    >
      <span
        aria-hidden
        style={{ width: 6, height: 6, borderRadius: 999, background: t.dot }}
      />
      {t.label}
    </span>
  );
}

export default StatusBadge;
